import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronRight,
  FaClone,
  FaRedo,
  FaSave,
  FaSearch,
  FaShieldAlt,
  FaToggleOn,
  FaUserCog
} from "react-icons/fa";
import Sidebar from "../../components/Sidebar/sidebar";
import Header from "../../components/Header";
import { isAuthenticated } from "../../utils/auth";
import api from "../../utils/api";
import "./PrivilegeManager.css";
import { API_BASE_URL } from '../../utils/apiBase';
import { confirmDialog } from '../../components/notifications/notificationService';

const getModuleKey = (moduleName) => moduleName || "Other";

const titleCase = (value = "") =>
  value
    .replace(/([A-Z])/g, " $1")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const ToggleSwitch = ({ checked, partial, disabled, onChange, label }) => (
  <button
    type="button"
    className={`privilege-switch ${checked ? "on" : ""} ${partial ? "partial" : ""}`}
    disabled={disabled}
    onClick={onChange}
    title={label}
    aria-pressed={checked}
  >
    <span />
  </button>
);

const PrivilegeManager = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [initialPrivileges, setInitialPrivileges] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [privilegeSearch, setPrivilegeSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("All Modules");
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedPrivilegeNodes, setExpandedPrivilegeNodes] = useState({});
  const [roleSummary, setRoleSummary] = useState([]);
  const [cloneRole, setCloneRole] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [privilegeRes, summaryRes] = await Promise.all([
          api.get(`${API_BASE_URL}/api/privileges`),
          api.get(`${API_BASE_URL}/api/privileges/roles/summary`)
        ]);

        setPrivileges(privilegeRes.data || []);
        setRoleSummary(summaryRes.data || []);
      } catch (err) {
        console.error("Error loading privileges:", err.response?.data || err);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const moduleNames = [...new Set(privileges.map((privilege) => getModuleKey(privilege.module)))];
    setExpandedModules((current) => {
      const next = { ...current };
      moduleNames.forEach((moduleName) => {
        if (next[moduleName] === undefined) {
          next[moduleName] = true;
        }
      });
      return next;
    });
  }, [privileges]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!userSearch.trim()) {
        setUsers([]);
        return;
      }

      try {
        const res = await api.get(
          `${API_BASE_URL}/api/privileges/users/search?q=${encodeURIComponent(userSearch)}`
        );
        setUsers(res.data || []);
      } catch (err) {
        console.error("Error searching users:", err.response?.data || err);
      }
    };

    const timer = setTimeout(loadUsers, 250);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const selectedPrivilegeSet = useMemo(
    () => new Set(selectedUser?.effectivePrivileges || selectedUser?.privileges || []),
    [selectedUser]
  );

  const rolePrivilegeSet = useMemo(
    () => new Set(selectedUser?.rolePrivileges || []),
    [selectedUser]
  );

  const roleGrantMap = selectedUser?.roleGrantMap || {};

  const groupedPrivileges = useMemo(() => {
    const grouped = privileges.reduce((acc, privilege) => {
      const module = getModuleKey(privilege.module);

      if (!acc[module]) {
        acc[module] = {
          rootPrivileges: [],
          childMap: {},
          allPrivileges: []
        };
      }

      acc[module].allPrivileges.push(privilege);

      if (privilege.parentKey) {
        if (!acc[module].childMap[privilege.parentKey]) {
          acc[module].childMap[privilege.parentKey] = [];
        }
        acc[module].childMap[privilege.parentKey].push(privilege);
      } else {
        acc[module].rootPrivileges.push(privilege);
      }

      return acc;
    }, {});

    Object.values(grouped).forEach((group) => {
      group.rootPrivileges.sort((a, b) => a.label.localeCompare(b.label));
      Object.values(group.childMap).forEach((children) =>
        children.sort((a, b) => a.label.localeCompare(b.label))
      );
    });

    return grouped;
  }, [privileges]);

  const moduleNames = useMemo(
    () => Object.keys(groupedPrivileges).sort((a, b) => a.localeCompare(b)),
    [groupedPrivileges]
  );

  const totalAssignedByRole = useMemo(
    () => roleSummary.reduce((sum, role) => sum + (Number(role.privilegeCount) || 0), 0),
    [roleSummary]
  );

  const getChildren = useCallback(
    (moduleName, parentKey) =>
      groupedPrivileges[moduleName]?.childMap[parentKey] || [],
    [groupedPrivileges]
  );

  const privilegeByKey = useMemo(
    () => new Map(privileges.map((privilege) => [privilege.key, privilege])),
    [privileges]
  );

  const getDescendants = useCallback(
    (moduleName, parentKey) => {
      const descendants = [];
      const visit = (key) => {
        getChildren(moduleName, key).forEach((child) => {
          descendants.push(child);
          visit(child.key);
        });
      };
      visit(parentKey);
      return descendants;
    },
    [getChildren]
  );

  const getAncestorKeys = useCallback((privilege) => {
    const ancestors = [];
    let parentKey = privilege.parentKey;

    while (parentKey) {
      ancestors.push(parentKey);
      parentKey = privilegeByKey.get(parentKey)?.parentKey || "";
    }

    return ancestors;
  }, [privilegeByKey]);

  const getModulePrivilegeKeys = (moduleName) => {
    const group = groupedPrivileges[moduleName];
    if (!group) return [];

    return group.allPrivileges.map((privilege) => privilege.key);
  };

  const setUserPrivileges = (nextPrivileges) => {
    setSelectedUser((current) => {
      if (!current) return current;
      const customPrivileges = [...new Set(nextPrivileges)].filter(
        (key) => !(current.rolePrivileges || []).includes(key)
      );
      const effectivePrivileges = [
        ...new Set([...(current.rolePrivileges || []), ...customPrivileges])
      ];
      return {
        ...current,
        privileges: customPrivileges,
        customPrivileges,
        effectivePrivileges
      };
    });
  };

  const selectUser = (user) => {
    const rolePrivileges = user.rolePrivileges || [];
    const customPrivileges = user.customPrivileges || user.privileges || [];
    const normalized = {
      ...user,
      privileges: customPrivileges,
      customPrivileges,
      rolePrivileges,
      effectivePrivileges: user.effectivePrivileges || [
        ...new Set([...rolePrivileges, ...customPrivileges])
      ],
      roleGrantMap: user.roleGrantMap || {}
    };
    setSelectedUser(normalized);
    setInitialPrivileges(normalized.customPrivileges || []);
  };

  const togglePrivilege = (moduleName, privilege) => {
    if (!selectedUser) return;

    const relatedKeys = [
      privilege.key,
      ...getDescendants(moduleName, privilege.key).map((child) => child.key)
    ];
    const isFullySelected = relatedKeys.every((key) => selectedPrivilegeSet.has(key));
    const nextSet = new Set(selectedUser.privileges || []);

    if (isFullySelected) {
      relatedKeys.forEach((key) => nextSet.delete(key));
    } else {
      relatedKeys.forEach((key) => nextSet.add(key));
      getAncestorKeys(privilege).forEach((key) => nextSet.add(key));
    }

    setUserPrivileges([...nextSet]);
  };

  const selectModule = (moduleName) => {
    if (!selectedUser) return;
    setUserPrivileges([...selectedUser.privileges, ...getModulePrivilegeKeys(moduleName)]);
  };

  const deselectModule = (moduleName) => {
    if (!selectedUser) return;
    const keys = new Set(getModulePrivilegeKeys(moduleName));
    setUserPrivileges(selectedUser.privileges.filter((key) => !keys.has(key)));
  };

  const selectAll = () => {
    if (!selectedUser) return;
    setUserPrivileges(privileges.map((privilege) => privilege.key));
  };

  const deselectAll = async () => {
    if (!selectedUser) return;
    if (await confirmDialog({ title: 'Remove all privileges?', message: 'This user will lose all currently selected access rights.', confirmText: 'Remove All' })) {
      setUserPrivileges([]);
    }
  };

  const resetChanges = async () => {
    if (!selectedUser) return;
    if (await confirmDialog({ title: 'Reset unsaved changes?', message: 'All privilege changes made in this session will be discarded.', confirmText: 'Reset' })) {
      setUserPrivileges(initialPrivileges);
    }
  };

  const cloneFromRole = async () => {
    if (!selectedUser || !cloneRole) return;

    if (!await confirmDialog({ title: 'Copy role privileges?', message: `Copy ${titleCase(cloneRole)} role privileges to ${selectedUser.name}?`, confirmText: 'Copy Privileges', tone: 'info' })) {
      return;
    }

    try {
      const res = await api.get(`${API_BASE_URL}/api/privileges/roles/${cloneRole}/privileges`);
      setUserPrivileges(res.data?.privileges || []);
    } catch (err) {
      console.error("Error cloning privileges:", err.response?.data || err);
      alert("Failed to clone privileges");
    }
  };

  const save = async () => {
    if (!selectedUser) return;
    if (!await confirmDialog({ title: 'Save privilege changes?', message: `Apply the selected privileges to ${selectedUser.name}?`, confirmText: 'Save Changes', tone: 'info' })) {
      return;
    }

    setSaving(true);
    try {
      await api.put(`${API_BASE_URL}/api/privileges/assign/${selectedUser._id}`, {
        privileges: selectedUser.privileges || []
      });
      setInitialPrivileges(selectedUser.privileges || []);
      alert("Privileges saved");
    } catch (err) {
      console.error("Error saving privileges:", err.response?.data || err);
      alert("Failed to save privileges");
    } finally {
      setSaving(false);
    }
  };

  const filteredModules = useMemo(() => {
    const search = privilegeSearch.trim().toLowerCase();

    const buildTree = (moduleName, privilege) => ({
      ...privilege,
      children: getChildren(moduleName, privilege.key).map((child) =>
        buildTree(moduleName, child)
      )
    });

    const filterTree = (node, moduleName) => {
      if (!search) return node;
      const selfMatches = [moduleName, node.label, node.key]
        .join(" ")
        .toLowerCase()
        .includes(search);
      if (selfMatches) return node;

      const children = node.children
        .map((child) => filterTree(child, moduleName))
        .filter(Boolean);
      return children.length ? { ...node, children } : null;
    };

    return moduleNames
      .filter((moduleName) => moduleFilter === "All Modules" || moduleName === moduleFilter)
      .map((moduleName) => {
        const group = groupedPrivileges[moduleName];
        const rootPrivileges = group.rootPrivileges
          .map((privilege) => filterTree(buildTree(moduleName, privilege), moduleName))
          .filter(Boolean);

        return {
          moduleName,
          rootPrivileges
        };
      })
      .filter((module) => module.rootPrivileges.length > 0);
  }, [moduleNames, groupedPrivileges, moduleFilter, privilegeSearch, getChildren]);

  const renderPrivilegeNode = (moduleName, privilege, depth = 0) => {
    const descendants = getDescendants(moduleName, privilege.key);
    const relatedKeys = [privilege.key, ...descendants.map((child) => child.key)];
    const selectedCount = relatedKeys.filter((key) => selectedPrivilegeSet.has(key)).length;
    const checked = selectedCount === relatedKeys.length;
    const partial = selectedCount > 0 && !checked;
    const roleGranted = rolePrivilegeSet.has(privilege.key);
    const fullyRoleGranted = relatedKeys.every((key) => rolePrivilegeSet.has(key));
    const children = privilege.children || [];
    const canCollapseChildren = depth >= 1 && children.length > 0;
    const isNodeExpanded = Boolean(expandedPrivilegeNodes[privilege.key]);
    const showChildren = children.length > 0 && (
      depth === 0 ||
      isNodeExpanded ||
      Boolean(privilegeSearch.trim())
    );

    return (
      <div
        key={privilege.key}
        className={`${depth === 0 ? "privilege-parent-card" : "privilege-tree-node"} ${checked ? "active" : ""} ${partial ? "partial" : ""} ${roleGranted ? "role-granted" : ""}`}
      >
        <div
          className="privilege-node-row"
          style={{
            paddingLeft: `${14 + (depth * 28)}px`,
            "--connector-left": `${17 + (Math.max(depth - 1, 0) * 28)}px`
          }}
        >
          {canCollapseChildren ? (
            <button
              type="button"
              className="privilege-node-expand"
              onClick={() =>
                setExpandedPrivilegeNodes((current) => ({
                  ...current,
                  [privilege.key]: !isNodeExpanded
                }))
              }
              aria-expanded={showChildren}
              title={`${showChildren ? "Collapse" : "Expand"} ${privilege.label}`}
            >
              {showChildren ? <FaChevronDown /> : <FaChevronRight />}
            </button>
          ) : (
            <span className="privilege-node-expand-spacer" />
          )}
          <ToggleSwitch
            checked={checked}
            partial={partial}
            disabled={!selectedUser || fullyRoleGranted}
            onChange={() => togglePrivilege(moduleName, privilege)}
            label={roleGranted ? `Granted via: ${roleGrantMap[privilege.key]}` : `${checked ? "Disable" : "Enable"} ${privilege.label}`}
          />
          <div className="privilege-text">
            <strong>{privilege.label}</strong>
            <span title={privilege.key}>{privilege.key}</span>
          </div>
          <span
            className={`state-pill ${roleGranted ? "role" : checked ? "active" : partial ? "partial" : ""}`}
            title={roleGranted ? `Granted via: ${roleGrantMap[privilege.key]}` : ""}
          >
            {roleGranted ? "Role" : checked ? "Active" : partial ? "Partial" : "Inactive"}
          </span>
        </div>

        {showChildren && (
          <div className="privilege-node-children">
            {children.map((child) => renderPrivilegeNode(moduleName, child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!isAuthenticated()) {
    return <Navigate to="/" />;
  }

  return (
    <div className="privilege-manager-container">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      <div className={`privilege-manager-content ${collapsed ? "collapsed" : ""}`}>
        <div className="privilege-sticky-header">
          <Header />
        </div>

        <div className="privilege-scroll-area">
          <section className="privilege-shell">
            <div className="privilege-hero">
              <div>
                <span className="privilege-eyebrow">
                  <FaShieldAlt /> System Settings / Access Control
                </span>
                <h1>Privilege Master</h1>
                <p>Assign module access, review role-granted permissions, and manage user-specific privilege rules.</p>
              </div>

              <div className="privilege-hero-stats">
                <span>Total Privileges</span>
                <strong>{privileges.length}</strong>
                <small>{moduleNames.length} modules configured</small>
              </div>
            </div>

            <div className="role-summary-grid">
              <div className="role-summary-card is-blue">
                <FaShieldAlt />
                <span>Privilege Library</span>
                <strong>{privileges.length}</strong>
                <small>{moduleNames.length} active modules</small>
              </div>
              <div className="role-summary-card is-green">
                <FaUserCog />
                <span>Role Coverage</span>
                <strong>{roleSummary.length}</strong>
                <small>roles available for cloning</small>
              </div>
              <div className="role-summary-card is-amber">
                <FaToggleOn />
                <span>Role Grants</span>
                <strong>{totalAssignedByRole}</strong>
                <small>privileges assigned across roles</small>
              </div>
              {roleSummary.map((role) => (
                <div key={role.role} className="role-summary-card is-teal">
                  <span>{titleCase(role.role)}</span>
                  <strong>{role.privilegeCount}</strong>
                  <small>{role.userCount} users with assigned privileges</small>
                </div>
              ))}
            </div>

            <div className="privilege-workspace">
              <aside className="privilege-user-panel">
                <div className="panel-title">
                  <FaUserCog />
                  <div>
                    <h3>User Selection</h3>
                    <p>Search an employee to edit custom access.</p>
                  </div>
                </div>

                <label className="privilege-search-box">
                  <FaSearch />
                  <input
                    placeholder="Search user by name"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                </label>

                <div className="user-result-list">
                  {users.map((user) => (
                    <button
                      type="button"
                      key={user._id}
                      className={`user-result-card ${selectedUser?._id === user._id ? "active" : ""}`}
                      onClick={() => selectUser(user)}
                    >
                      <strong>{user.name}</strong>
                      <span>{user.email}</span>
                      <small>
                        {titleCase(user.role)} · {user.roleName || "No assigned role"} · {user.status || "active"}
                      </small>
                    </button>
                  ))}
                  {userSearch && users.length === 0 && (
                    <div className="empty-user-result">No users found.</div>
                  )}
                </div>
              </aside>

              <main className="privilege-editor-panel">
                <div className="privilege-editor-toolbar">
                  <div>
                    <h3>
                      {selectedUser
                        ? `Privileges for ${selectedUser.name}`
                        : "Select a user to assign privileges"}
                    </h3>
                    <p>
                      {selectedUser
                        ? `${selectedUser.effectivePrivileges?.length || 0} total privileges (${selectedUser.rolePrivileges?.length || 0} role, ${selectedUser.privileges?.length || 0} custom)`
                        : "Search for a user on the left to begin."}
                    </p>
                  </div>

                  <div className="privilege-save-actions">
                    <button type="button" onClick={selectAll} disabled={!selectedUser}>
                      <FaToggleOn /> Select All
                    </button>
                    <button type="button" onClick={deselectAll} disabled={!selectedUser}>
                      Deselect All
                    </button>
                    <select
                      value={cloneRole}
                      onChange={(e) => setCloneRole(e.target.value)}
                      disabled={!selectedUser}
                      title="Copy privileges from users with this role"
                    >
                      <option value="">Clone from role</option>
                      {roleSummary.map((role) => (
                        <option key={role.role} value={role.role}>
                          {titleCase(role.role)}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={cloneFromRole} disabled={!selectedUser || !cloneRole}>
                      <FaClone /> Clone
                    </button>
                    <button type="button" onClick={resetChanges} disabled={!selectedUser}>
                      <FaRedo /> Reset
                    </button>
                    <button type="button" className="primary-save" onClick={save} disabled={!selectedUser || saving}>
                      <FaSave /> {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>

                <div className="privilege-filter-row">
                  <label className="privilege-search-box wide">
                    <FaSearch />
                    <input
                      placeholder="Search privileges by name, key, or module"
                      value={privilegeSearch}
                      onChange={(e) => setPrivilegeSearch(e.target.value)}
                    />
                  </label>

                  <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
                    <option>All Modules</option>
                    {moduleNames.map((moduleName) => (
                      <option key={moduleName}>{moduleName}</option>
                    ))}
                  </select>
                </div>

                <div className="privilege-modules">
                  {filteredModules.map(({ moduleName, rootPrivileges }) => {
                    const moduleKeys = getModulePrivilegeKeys(moduleName);
                    const selectedModuleKeys = moduleKeys.filter((key) => selectedPrivilegeSet.has(key));
                    const isExpanded = expandedModules[moduleName] !== false;

                    return (
                      <article key={moduleName} className="privilege-module-card">
                        <header className="module-card-head">
                          <button
                            type="button"
                            className="module-expand-btn"
                            onClick={() =>
                              setExpandedModules((current) => ({
                                ...current,
                                [moduleName]: !isExpanded
                              }))
                            }
                          >
                            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            <span>{moduleName}</span>
                          </button>

                          <div className="module-head-meta">
                            <span>{selectedModuleKeys.length}/{moduleKeys.length} enabled</span>
                            <button type="button" onClick={() => selectModule(moduleName)} disabled={!selectedUser}>
                              Select All
                            </button>
                            <button type="button" onClick={() => deselectModule(moduleName)} disabled={!selectedUser}>
                              Deselect
                            </button>
                          </div>
                        </header>

                        <div className={`module-card-body ${isExpanded ? "open" : ""}`}>
                          {rootPrivileges.map((privilege) =>
                            renderPrivilegeNode(moduleName, privilege)
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </main>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivilegeManager;
